import React, { useEffect, useState } from "react";
import { useAdminAgent } from "./useAdminAgent";
import ChatPanel from "./ChatPanel";

const AssistantBubble: React.FC = () => {
  const [open, setOpen] = useState(false);
  const {
    messages,
    isThinking,
    send,
    chats,
    chatsLoading,
    selectedChatId,
    selectChat,
    newChat,
    deleteChat,
  } = useAdminAgent();

  // Esc closes the modal. Global because backdrop / panel children may steal
  // focus (e.g. the textarea) and we still want Esc to dismiss.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock background scroll while the modal is open so the page underneath
  // doesn't scroll when the user wheels inside the chat.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open assistant"
          title="Ask the assistant"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 20px 12px 16px",
            borderRadius: 999,
            border: "none",
            background: "#3949ab",
            color: "#fff",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 8px 24px rgba(57,73,171,0.35)",
            transition: "transform 0.15s, box-shadow 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 10px 28px rgba(57,73,171,0.45)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(57,73,171,0.35)";
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>✦</span>
          Ask AI
        </button>
      ) : null}

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1001,
            background: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            animation: "ai-fade-in 0.15s ease-out",
          }}
        >
          <style>{`
            @keyframes ai-fade-in { from { opacity: 0; } to { opacity: 1; } }
            @keyframes ai-pop-in { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
          `}</style>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1040px, 100%)",
              height: "min(720px, 90vh)",
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              animation: "ai-pop-in 0.18s ease-out",
            }}
          >
            <ChatPanel
              messages={messages}
              isThinking={isThinking}
              onSend={send}
              onClose={() => setOpen(false)}
              chats={chats}
              chatsLoading={chatsLoading}
              selectedChatId={selectedChatId}
              onSelectChat={selectChat}
              onNewChat={newChat}
              onDeleteChat={deleteChat}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AssistantBubble;
